import { ILeadFormModel } from '@user/components/Leads/AddLeadForm/typings'
import {
  addLeadAddress,
  addLeadAltPhoneNumber,
  addLeadCompany,
  addLeadDivisionId,
  addLeadEmailAddress,
  addLeadFirstName,
  addLeadLastName,
  addLeadLocationTypeId,
  addLeadPhoneNumber,
  addLeadPreferredMethodOfContact,
  addLeadPreferredTimeOfContact,
  EntityFormFieldName,
} from '@user/components/Settings/EntityFormSettings'
import {
  NAME_REQUIREMENT_OR,
  NameRequirementMode,
} from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { FieldSettingsMap } from '@user/store/entityFormSettings'
import { useFormikContext } from 'formik'
import { FieldNames } from '../FieldNames'

type Props = {
  disabled?: boolean
  maxEmails?: number
  settingsMap: FieldSettingsMap
  fieldNames: FieldNames
  fieldNamePrefix?: string
  nameRequirementMode: NameRequirementMode
}

/**
 * Maps a name-group `EntityFormFieldName` to the corresponding key on
 * `FieldNames` for this `LocationInformation` instance, so lookups against
 * `values` use the instance's own field-name/prefix wiring rather than a
 * hardcoded `'firstName'` etc.
 */
function fieldNameForNameField(
  id: EntityFormFieldName,
  fieldNames: FieldNames,
): string {
  switch (id) {
    case addLeadFirstName:
      return fieldNames.firstName
    case addLeadLastName:
      return fieldNames.lastName
    case addLeadCompany:
      return fieldNames.company
    default:
      return ''
  }
}

export function useData(props: Props) {
  const {
    fieldNames,
    fieldNamePrefix,
    settingsMap,
    maxEmails = 3,
    nameRequirementMode,
  } = props

  const { values } = useFormikContext<ILeadFormModel>()

  //region REQUIRED
  const [requiredLocationTypeId, showLocationTypeId] = settingsMap.get(
    addLeadLocationTypeId,
  ) ?? [false, true]

  const [requiredDivisionId, showDivisionId] = settingsMap.get(
    addLeadDivisionId,
  ) ?? [false, true]

  const [requiredAddress, showAddress] = settingsMap.get(addLeadAddress) ?? [
    false,
    true,
  ]

  const [settingsRequiredFirstName, showFirstName] = settingsMap.get(
    addLeadFirstName,
  ) ?? [false, true]

  const [settingsRequiredLastName, showLastName] = settingsMap.get(
    addLeadLastName,
  ) ?? [false, true]

  const [settingsRequiredCompany, showCompany] = settingsMap.get(
    addLeadCompany,
  ) ?? [false, true]

  const [requiredPhone, showPhone] = settingsMap.get(addLeadPhoneNumber) ?? [
    false,
    true,
  ]

  const [requiredEmail, showEmail] = settingsMap.get(addLeadEmailAddress) ?? [
    false,
    true,
  ]
  const [requiredAltPhoneNumber, showAltPhoneNumber] = settingsMap.get(
    addLeadAltPhoneNumber,
  ) ?? [false, true]

  const [requiredPreferredMethodOfContact, showPreferredMethodOfContact] =
    settingsMap.get(addLeadPreferredMethodOfContact) ?? [false, true]

  const [requiredPreferredTimeOfContact, showPreferredTimeOfContact] =
    settingsMap.get(addLeadPreferredTimeOfContact) ?? [false, true]

  //endregion

  //region NAME REQUIREMENT MODE
  // Which name-group members are currently required, per this component's own
  // settingsMap (shared across the service-location and Bill To instances,
  // since the persisted `required` setting is entity-level, not per-prefix).
  const requiredNameFields: EntityFormFieldName[] = [
    settingsRequiredFirstName ? addLeadFirstName : null,
    settingsRequiredLastName ? addLeadLastName : null,
    settingsRequiredCompany ? addLeadCompany : null,
  ].filter((id): id is EntityFormFieldName => id !== null)

  // Whether *this* instance's own prefixed values satisfy the group — never
  // the other instance's (service location vs. Bill To are independent).
  const isNameFieldFilled = (id: EntityFormFieldName) =>
    (
      values[fieldNamePrefix + fieldNameForNameField(id, fieldNames)] ?? ''
    ).trim() !== ''

  const nameValuesFilled = requiredNameFields.some(isNameFieldFilled)

  const orModeSatisfied =
    nameRequirementMode === NAME_REQUIREMENT_OR &&
    requiredNameFields.length >= 2 &&
    nameValuesFilled

  // Once the group is satisfied in "or" mode, the asterisk drops only from
  // the sibling fields that are still blank — the field the user actually
  // filled in keeps showing as required, since it is one (per the confirmed
  // AC: "the asterisk for the OTHER, not provided field no longer displays").
  const requiredFirstName =
    settingsRequiredFirstName &&
    (!orModeSatisfied || isNameFieldFilled(addLeadFirstName))
  const requiredLastName =
    settingsRequiredLastName &&
    (!orModeSatisfied || isNameFieldFilled(addLeadLastName))
  const requiredCompany =
    settingsRequiredCompany &&
    (!orModeSatisfied || isNameFieldFilled(addLeadCompany))
  //endregion

  const disableAddEmailButton =
    values[fieldNamePrefix + fieldNames.emailAddresses].length >= maxEmails

  const phoneExtDisabled = !values[fieldNamePrefix + fieldNames.phone]

  const altPhoneExtDisabled = !values[fieldNamePrefix + fieldNames.altPhone]

  return {
    altPhoneExtDisabled,
    requiredLocationTypeId,
    showLocationTypeId,
    requiredDivisionId,
    showDivisionId,
    requiredAddress,
    showAddress,
    requiredFirstName,
    showFirstName,
    requiredLastName,
    showLastName,
    requiredCompany,
    showCompany,
    disableAddEmailButton,
    requiredPhone,
    showPhone,
    requiredEmail,
    showEmail,
    requiredAltPhoneNumber,
    showAltPhoneNumber,
    requiredPreferredMethodOfContact,
    showPreferredMethodOfContact,
    requiredPreferredTimeOfContact,
    showPreferredTimeOfContact,
    phoneExtDisabled,
  }
}
